import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormLabel,
    Input,
    Text,
    useColorModeValue,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import ConfirmDialog from "../../../components/ConfirmDialog";
import FormModal from "../../../components/FormModal";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { typography } from 'theme/tokens';
import { useElevatedAdminAccess } from "../hooks/useIsElevatedAdmin";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useUpdateOrganizationRecordingRetention } from "../hooks/useUpdateOrganizationRecordingRetention";
import { isRetentionLowering } from "../utils/recordingRetention";

// Server fallbacks for an API response that predates the fields — the
// platform defaults (RECORDING_RETENTION_DAYS / RECORDING_RETENTION_MAX_DAYS).
const FALLBACK_DAYS = 3;
const FALLBACK_MAX_DAYS = 90;
const MIN_DAYS = 1;

// Court-recording retention (docs/automation/recording-lifecycle-status.md,
// B4). EVERY admin of an org with recording sees the effective value,
// read-only. Only elevated (platform) admins get the editor: the API 403s
// everyone else, because LOWERING the value makes the next hourly sweep delete
// that org's older recordings irreversibly — which is also why a lowering save
// goes through a danger confirmation.
export default function RecordingRetentionSettingsCard() {
    // Goes through the fail-closed hook, not the raw query. This card was added
    // after run 111 classified the elevation read, so it arrived carrying the
    // exact pattern that run removed from five other surfaces:
    // `const { data: isElevatedAdmin } = useIsElevatedAdmin()` collapses an
    // `undefined` from a refused or in-flight read into `false`. All three
    // branches below are already fail-closed for a falsy value, so the
    // rendered result is unchanged for every state EXCEPT that `isElevated` is
    // now a real boolean that is true only for an ANSWERED `true` — the page
    // itself renders the one `ElevationUnknownNotice` that says so.
    const { isElevated: isElevatedAdmin } = useElevatedAdminAccess();
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();
    const editor = useDisclosure();
    const confirm = useDisclosure();

    const currentDays = selectedOrganization?.recordingRetentionDays ?? FALLBACK_DAYS;
    const currentIsDefault = selectedOrganization?.recordingRetentionIsDefault ?? true;
    const maxDays = selectedOrganization?.recordingRetentionMaxDays ?? FALLBACK_MAX_DAYS;

    const [useDefault, setUseDefault] = useState<boolean>(currentIsDefault);
    const [days, setDays] = useState<number>(currentDays);

    const { mutateAsync, isLoading } = useUpdateOrganizationRecordingRetention(
        selectedOrganization?.id || '',
        () => {
            editor.onClose();
            confirm.onClose();
            toast({
                title: translate('organization.recording_retention_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        },
    );

    if (!selectedOrganization) return null;
    // Nothing to retain for an org without a recorder, unless a platform admin
    // wants to set it up before the box goes live.
    if (!selectedOrganization.recordingAvailable && !isElevatedAdmin) return null;

    const nextValue: number | null = useDefault ? null : days;
    const isValid =
        nextValue === null ||
        (Number.isInteger(nextValue) && nextValue >= MIN_DAYS && nextValue <= maxDays);

    const save = async () => {
        try {
            await mutateAsync({ days: nextValue });
        } catch (err: any) {
            // A 400 carries the API's own range message; surface it as-is.
            toast({
                title: translate('error'),
                description: err?.message,
                status: 'error',
                duration: 4000,
                position: 'top-right',
            });
        }
    };

    const handleSubmit = () => {
        if (!isValid || isLoading) return;
        if (isRetentionLowering(currentDays, currentIsDefault, nextValue)) {
            confirm.onOpen();
            return;
        }
        void save();
    };

    const openEditor = () => {
        setUseDefault(currentIsDefault);
        setDays(currentDays);
        editor.onOpen();
    };

    return (
        <>
            <PageCard title={translate('organization.recording_retention_title')}>
                <Box p={4}>
                    <Box mb={2} color={textColorPrimary}>
                        <strong>{translate('organization.recording_retention_current', { days: currentDays })}</strong>
                        {' '}
                        <Text as="span" fontSize="sm" color="gray.500">
                            {currentIsDefault
                                ? translate('organization.recording_retention_is_default')
                                : translate('organization.recording_retention_is_custom')}
                        </Text>
                    </Box>
                    <Text fontSize="sm" color="gray.500">
                        {translate('organization.recording_retention_helper')}
                    </Text>
                    {isElevatedAdmin ? (
                        <Button mt={3} size="md" colorScheme="brand" onClick={openEditor}>
                            {translate('organization.recording_retention_edit')}
                        </Button>
                    ) : (
                        <Text mt={2} fontSize="xs" color="gray.500">
                            {translate('organization.recording_retention_read_only')}
                        </Text>
                    )}
                </Box>
            </PageCard>

            {isElevatedAdmin && (
                <FormModal
                    isOpen={editor.isOpen}
                    onClose={editor.onClose}
                    title={translate('organization.recording_retention_edit')}
                    onSubmit={handleSubmit}
                    submitLabel={translate('save')}
                    cancelLabel={translate('cancel')}
                    isSubmitting={isLoading}
                    isSubmitDisabled={!isValid}
                    colorScheme="warning"
                >
                    <Text fontSize={14}>
                        {translate('organization.recording_retention_range', { max: maxDays })}
                    </Text>
                    <Checkbox
                        mt={4}
                        isChecked={useDefault}
                        onChange={(e) => setUseDefault(e.target.checked)}
                    >
                        {translate('organization.recording_retention_use_default')}
                    </Checkbox>
                    <Flex direction="row" justify="center" align="center" py={4}>
                        <Input
                            w={28}
                            type="number"
                            min={MIN_DAYS}
                            max={maxDays}
                            step={1}
                            isDisabled={useDefault}
                            aria-label={translate('organization.recording_retention_days_label')}
                            value={Number.isFinite(days) ? days : ''}
                            onChange={(e) => {
                                const parsed = Number.parseInt(e.target.value, 10);
                                setDays(Number.isNaN(parsed) ? Number.NaN : parsed);
                            }}
                        />
                        <FormLabel {...typography.formLabel} mt={2} ml={2}>
                            {translate('organization.recording_retention_days_label')}
                        </FormLabel>
                    </Flex>
                    <Text fontSize={14} color="orange.600">
                        {translate('organization.recording_retention_lowering_warning')}
                    </Text>
                </FormModal>
            )}

            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={confirm.onClose}
                onConfirm={() => void save()}
                isConfirming={isLoading}
                intent="danger"
                title={translate('organization.recording_retention_confirm_title')}
                body={translate('organization.recording_retention_lowering_warning')}
                confirmLabel={translate('organization.recording_retention_confirm_action')}
                cancelLabel={translate('cancel')}
            />
        </>
    );
}
